import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, signIn, signOut, fetchAuthSession, SignInInput } from 'aws-amplify/auth';
import { isLocalMode } from '../utils/localMode.ts';
import { clearBoardWorkingState } from '../utils/boardLocalState.ts';
import { clearDataCache } from '../data/queryClient.ts';

// Cognito group that grants full read-write access. Must match ADMIN_GROUP in
// backend/src/utils/auth.ts and the Terraform aws_cognito_user_group resource.
export const ADMIN_GROUP = 'Admin';

export type Role = 'admin' | 'user';

interface AuthContextType {
  user: any | null;
  loading: boolean;
  error: string | null;
  /** Cognito groups from the caller's token (e.g. ["Admin"]). */
  groups: string[];
  /** The caller's tenant, from the custom:tenantId ID-token claim (display/UX only —
   *  the backend is the security boundary). Null when unauthenticated or the claim is absent. */
  customerId: string | null;
  /** True when the user is in the Admin group (may perform mutations). */
  isAdmin: boolean;
  /** Coarse role derived from groups: 'admin' if in the Admin group, else 'user'. */
  role: Role;
  /**
   * NOT the app's sign-in path — `components/Login.tsx` is, and it is the only
   * thing that implements Cognito's challenge steps (new password, TOTP setup,
   * TOTP code). This resolves ONLY when Cognito signs the user in outright, and
   * THROWS, naming the step, on any challenge. See the implementation note.
   */
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

// Reads the cognito:groups claim from the session tokens. The claim is present
// on both the ID and access tokens; the API uses the ID token, so prefer it.
function extractGroups(session: { tokens?: any } | null): string[] {
  const fromId = session?.tokens?.idToken?.payload?.['cognito:groups'];
  const fromAccess = session?.tokens?.accessToken?.payload?.['cognito:groups'];
  const raw = fromId ?? fromAccess;
  return Array.isArray(raw) ? raw.map((g: unknown) => String(g)) : [];
}

// Reads the custom:tenantId tenant claim from the ID token (the token the API uses;
// custom attributes ride the ID token, not the access token).
function extractCustomerId(session: { tokens?: any } | null): string | null {
  const raw = session?.tokens?.idToken?.payload?.['custom:tenantId'];
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkUser = async (retryCount = 0) => {
    // Local no-AWS mode: there is no Cognito. Auto-authenticate as an Admin so the
    // app is a full read-write playground. This branch only runs when the app was
    // built with REACT_APP_LOCAL_MODE=true (never in a deployed build).
    if (isLocalMode()) {
      setUser({ username: 'local-admin', userId: 'local-admin' });
      setGroups([ADMIN_GROUP]); // → isAdmin === true
      setCustomerId('local-customer'); // matches the local backend's fake tenant
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Force refresh the session to ensure we have the latest tokens
      const session = await fetchAuthSession({ forceRefresh: true });

      if (session.tokens?.accessToken) {
        // If we have a valid session, get the user
        const currentUser = await getCurrentUser();
        setUser(currentUser);
        setGroups(extractGroups(session));
        setCustomerId(extractCustomerId(session));
        setError(null);
      } else {
        setUser(null);
        setGroups([]);
        setCustomerId(null);
      }
    } catch (err) {
      console.log('checkUser error:', err, 'retry count:', retryCount);

      // Retry up to 2 times with a short delay
      // This handles race conditions where the session is being established
      if (retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return checkUser(retryCount + 1);
      }

      setUser(null);
      setGroups([]);
      setCustomerId(null);
      // Don't set error here - user just isn't authenticated
    } finally {
      setLoading(false);
    }
  };

  /**
   * NOT the sign-in path. `components/Login.tsx` is.
   *
   * ## What was wrong with it
   *
   * Cognito sign-in is a state machine: `signIn` returns a
   * `nextStep.signInStep` — CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED,
   * CONTINUE_SIGN_IN_WITH_TOTP_SETUP, CONFIRM_SIGN_IN_WITH_TOTP_CODE, and
   * eleven more — that has to be answered with `confirmSignIn`. This function
   * only ever looked at `result.isSignedIn`. Every challenge fell out of the
   * bottom and the promise RESOLVED, so a caller was told the login had
   * succeeded while the user was not signed in, no factor had been collected,
   * and no UI had been shown for the outstanding step. Exactly the silent
   * fall-through that `Login.tsx` was rewritten to eliminate.
   *
   * With `mfa_configuration = "ON"` on the user pool that is now EVERY sign-in
   * (TOTP setup on the first, a TOTP code on all the rest) — so this function
   * can no longer succeed at all, and silence is the worst way for it to fail.
   *
   * ## Why it throws instead of being deleted
   *
   * `login` is a member of the exported `AuthContextType`; removing it is a
   * breaking change to a public API. It is verified unused at the time of this
   * change — nothing in `src/` destructures `login` from `useAuth()`, which
   * `contexts/__tests__/AuthContextLogin.test.tsx` pins with a source scan — so
   * making it loud costs nothing today and converts a future accidental use
   * into an immediate, named failure at the call site instead of a phantom
   * successful login.
   *
   * If you ever need programmatic sign-in, implement the challenge steps rather
   * than dropping the throw. `Login.tsx`'s `applyNextStep` is the working model.
   */
  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const signInInput: SignInInput = {
        username,
        password,
      };

      const result = await signIn(signInInput);

      if (result.isSignedIn) {
        await checkUser();
        return;
      }

      // Cast: `nextStep.signInStep` is a union of literals on the SDK type, but
      // we want whatever actually arrived, including from a shape we don't know.
      const step: string = (result as any)?.nextStep?.signInStep || 'UNKNOWN_STEP';
      throw new Error(
        `AuthContext.login() cannot complete this sign-in: Cognito returned the ` +
          `challenge "${step}", which this function does not implement. Use the ` +
          `Login screen (components/Login.tsx), which handles the full challenge flow.`
      );
    } catch (err: any) {
      setError(err.message || 'Failed to login');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await signOut();
      // Board working notes name real providers; don't leave them for the next user of this browser.
      try { clearBoardWorkingState(); } catch { /* localStorage unavailable */ }
      // The in-memory data cache holds this user's tenant data (and, for Admins, special-category
      // fields such as isPregnant). Drop it before the login screen renders for whoever is next.
      clearDataCache();
      setUser(null);
      setGroups([]);
      setCustomerId(null);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to logout');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (err) {
      console.error('Failed to get access token:', err);
      return null;
    }
  };

  const isAdmin = groups.includes(ADMIN_GROUP);

  const value: AuthContextType = {
    user,
    loading,
    error,
    groups,
    customerId,
    isAdmin,
    role: isAdmin ? 'admin' : 'user',
    login,
    logout,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
